<template>
  <div
    :id="selectId"
    ref="reference"
    class="typeahead typeahead--searchable position-relative"
    :aria-disabled="props.disabled"
    :class="{ 'typeahead--disabled': props.disabled }"
    v-bind="rootAttrs"
  >
    <div
      :id="typeaheadComboboxId"
      ref="typeAheadContainer"
      :class="{
        'typeahead--open': isDropDownOpen,
        'typeahead__dropdown-toggle--focused': isFocused
      }"
      class="typeahead__dropdown-toggle position-relative"
    >
      <div class="typeahead__selected-options" role="presentation" @click="handleOptionsClick">
        <span
          v-if="!hasInput"
          ref="selectedPlaceholderRef"
          class="typeahead__selected"
          :style="ghostOpacityDynamicStyles"
          :class="{ 'is-placeholder': isPlaceholder }"
          >{{ model?.text || ghostValue || placeholder }}</span
        >
        <input
          :id="typeaheadSearchId"
          ref="typeaheadQuery"
          v-model="query"
          type="search"
          role="combobox"
          :aria-disabled="props.disabled"
          aria-autocomplete="list"
          :aria-expanded="isDropDownOpen"
          :aria-controls="typeaheadListboxId"
          :aria-label="typeaheadAriaLabel"
          :aria-labelledby="typeaheadAriaLabelledBy"
          autocomplete="off"
          :aria-activedescendant="ariaActiveDescendantId"
          class="typeahead__search"
          :aria-describedby="typeaheadDescribedBy"
          @focus="handleTypeAheadFocus"
          @blur="handleTypeAheadBlur"
          @input="handleOnInput"
        />
        <span :id="typeaheadInstructionsId" class="visually-hidden">
          Press enter to open the dropdown menu.
        </span>
        <div :id="typeaheadAnnouncementId" class="visually-hidden" aria-live="polite"></div>
      </div>
      <div class="typeahead__actions ps-space-xxs">
        <button
          v-if="isDisplayClearButton"
          type="button"
          title="clear selected"
          :aria-disabled="props.disabled"
          tabindex="0"
          aria-label="clear selected"
          class="typeahead__action me-space-sm"
          :class="actionClasses"
          @click="clearSelected"
        >
          <font-awesome-icon icon="fa-close" />
        </button>
        <button
          ref="dropdownRef"
          type="button"
          class="typeahead__action me-space-xxs"
          :class="actionClasses"
          :aria-disabled="props.disabled"
          aria-label="expand selection"
          @click="handleDropdownClick"
        >
          <font-awesome-icon
            icon="fa-angle-down"
            :class="{ 'fa-rotate-180': isDropDownOpen }"
            class="typeahead__dropdown-icon"
          />
        </button>
      </div>
    </div>

    <Transition name="fade" @before-leave="handleCollapseHide" @enter="handleCollapseShown">
      <ul
        v-show="isDropDownOpen"
        :id="typeaheadListboxId"
        ref="dropdownList"
        role="listbox"
        :aria-label="typeaheadAriaLabel"
        :style="{ ...dynamicStyles, ...floatingStyles }"
        tabindex="-1"
        class="typeahead__dropdown-menu"
        :class="{ 'typeahead__dropdown-menu--shown': isDropDownOpen }"
      >
        <template v-if="results.length > 0">
          <li
            v-for="({ item }, i) in results"
            :id="`${selectId}_option_${i}`"
            :key="item.text"
            :ref="(el) => functionRef(el, i)"
            role="option"
            :data-value="item.text"
            class="typeahead__dropdown-option"
            :class="{
              'typeahead__dropdown-option--highlighted': hoverIndex === i
            }"
            :aria-selected="hoverIndex === i"
            @mouseenter="handleHover(i, item)"
            @click="handleChangeOption(item)"
          >
            {{ item.text }}
          </li>
        </template>
        <template v-else>
          <li class="typeahead__dropdown-option--empty">No results found.</li>
        </template>
      </ul>
    </Transition>
  </div>
</template>
<script lang="ts" setup generic="T extends OptionType">
import type { VariantOption } from "@/types/types";
import { autoUpdate, flip, offset, shift, useFloating } from "@floating-ui/vue";
import { useFuse, type UseFuseOptions } from "@vueuse/integrations/useFuse";

import type { FuseResult, FuseSortFunctionArg } from "fuse.js";
/*
 ********************************************
 *  types
 ********************************************
 */
export type OptionType = {
  value: string | null;
  text: string;
};

export type TypeAheadSelectSortFunc = (a: FuseSortFunctionArg, b: FuseSortFunctionArg) => number;

type Props = {
  options: T[];
  placeholder?: string;
  isEditable?: boolean;
  height?: string;
  highlightBgColor?: VariantOption;
  highlightTextColor?: VariantOption;
  actionVarint?: VariantOption;
  disabled?: boolean;
  preselectOpacity?: string;
  id: string;
  labelId?: string;
  clearable?: boolean;
  alphaSort?: boolean;
  sortFn?: TypeAheadSelectSortFunc;
};

/*
 ********************************************
 *  data
 ********************************************
 */
const isFocused = ref<boolean>(false);
const isDropDownOpen = ref<boolean>(false);
const query = ref<string>("");
const typeAheadContainer = ref<HTMLElement | null>(null);
const typeaheadQuery = ref<HTMLElement | null>(null);
const hoverIndex = ref<number>(0);
const isHoverActive = ref<boolean>(false);
const dropdownList = ref<HTMLElement | null>(null);
const selectedPlaceholderRef = ref<HTMLElement | null>(null);
const dropdownRef = ref<HTMLElement | null>();
const resultElements = ref<Array<Element | globalThis.ComponentPublicInstance | null> | []>([]);
const selectedElement = ref<HTMLElement | null>(null);
/*
 ********************************************
 *  props
 ********************************************
 */
const props = withDefaults(defineProps<Props>(), {
  placeholder: "Search for options",
  height: "350px",
  highlightBgColor: "secondary",
  highlightTextColor: "dark-3",
  actionVarint: "dark-2",
  preselectOpacity: "0.6", // minimum for color contrast requirement
  disabled: false,
  clearable: true
});
const attrs = useAttrs();
const rootAttrs = computed(() => {
  const {
    "aria-label": _ariaLabel,
    "aria-labelledby": _ariaLabelledby,
    "aria-describedby": _ariaDescribedby,
    ...remainingAttrs
  } = attrs;

  return remainingAttrs;
});

/**
 * Emits for TypeAheadSelect component.
 *
 * @emits hidden - Emitted when the dropdown is hidden.
 * @emits shown - Emitted when the dropdown is shown.
 * @emits changed - Emitted after a user initiated change of the modal value.
 * @emits searching - Emitted when searching is performed.
 * @emits cleared - Emitted when the input is cleared.
 */
const emits = defineEmits<{
  (e: "hidden" | "shown" | "cleared"): void;
  (e: "changed", val: OptionType | null): void;
  (e: "searching", query: string): void;
}>();

/*
 ********************************************
 * external composable
 ********************************************
 */

//  2 way binding
const model = defineModel<OptionType | null>({ required: true });

const reference = ref(null);
const { floatingStyles } = useFloating(reference, dropdownList, {
  whileElementsMounted: autoUpdate,
  strategy: "absolute",
  open: isDropDownOpen,
  transform: false,
  placement: "bottom",
  middleware: [flip(), offset(-2.2), shift()]
});

// fuse's default sort
const defaultFuseSort = (a: FuseSortFunctionArg, b: FuseSortFunctionArg) => {
  return a.score === b.score ? (a.idx < b.idx ? -1 : 1) : a.score < b.score ? -1 : 1;
};

// alpha sort for fuse
const alphaFuseSort = (a: FuseSortFunctionArg, b: FuseSortFunctionArg) => {
  if (a.matches && b.matches && a.matches[0].value && b.matches[0].value) {
    if (a.matches[0].value < b.matches[0].value) {
      return -1;
    }
    if (a.matches[0].value > b.matches[0].value) {
      return 1;
    }
    return 0;
  } else return 0;
};

// prioritize custom sort function then alpha, then use default
const sortingMethod = computed(() => {
  if (props.sortFn) {
    return props.sortFn;
  } else if (props.alphaSort == true) {
    return alphaFuseSort;
  } else {
    return defaultFuseSort;
  }
});

const fuseOptions = computed<UseFuseOptions<T>>(() => ({
  fuseOptions: {
    keys: ["text"],
    isCaseSensitive: false,
    includeScore: false,
    useExtendedSearch: true,
    includeMatches: true,
    minMatchCharLength: 1,
    shouldSort: true,
    findAllMatches: false,
    location: 0,
    threshold: 0.4,
    distance: 300,
    sortFn: sortingMethod.value
  },
  matchAllWhenSearchEmpty: true
}));

const searchOptions = ref<T[]>([]) as Ref<T[]>;
const { results } = useFuse(query, searchOptions, fuseOptions);

const handleDocumentClick = (event: MouseEvent) => {
  const target = event.target;
  if (!(target instanceof Node) || !typeAheadContainer.value?.contains(target)) {
    handleDropDownClose();
    selectedElement.value = null;
    query.value = "";
  }
};

const handleKeyDown = (event: KeyboardEvent) => {
  if (event.key === "ArrowDown" && isDropDownOpen.value) {
    event.preventDefault();
    if (hoverIndex.value < results.value.length - 1) {
      hoverIndex.value++;
    }
    return;
  }

  if (event.key === "ArrowUp" && isDropDownOpen.value) {
    if (hoverIndex.value > 0) {
      event.preventDefault();
      hoverIndex.value--;
    }
    return;
  }

  if ((event.key === "Tab" || event.key === "Escape") && isDropDownOpen.value) {
    handleDropDownClose();
    return;
  }

  if (event.key !== "Enter" || props.disabled) {
    return;
  }

  if (isFocused.value && !isDropDownOpen.value) {
    event.preventDefault();
    isDropDownOpen.value = true;
    return;
  }

  if (isDropDownOpen.value) {
    event.preventDefault();
    if (results.value[hoverIndex.value] && results.value[hoverIndex.value].item) {
      handleChangeOption(results.value[hoverIndex.value].item);
      query.value = "";
    }
    typeaheadQuery.value?.blur();
    dropdownRef.value?.focus();
  }
};

const outsideClickListenerOptions = { capture: true, passive: true };

onMounted(() => {
  document.addEventListener("click", handleDocumentClick, outsideClickListenerOptions);
  document.addEventListener("keydown", handleKeyDown);
});

onBeforeUnmount(() => {
  document.removeEventListener("click", handleDocumentClick, outsideClickListenerOptions);
  document.removeEventListener("keydown", handleKeyDown);
});

const handleOptionsClick = () => {
  if (props.disabled) {
    return;
  }
  typeaheadQuery.value?.focus();
  isDropDownOpen.value = true;
};
/*
 ********************************************
 *  computed
 ********************************************
 */

const ariaActiveDescendantId = computed<string | undefined>(() => {
  if (isDropDownOpen.value && results.value.length > 0) {
    return `${selectId.value}_option_${hoverIndex.value}`;
  }
  return undefined;
});

const ghostValue = computed<string | null>(() => {
  return selectedElement.value?.innerText as string;
});

const isDisplayClearButton = computed(() => {
  return model.value !== null && props.clearable;
});

const selectId = computed(() => props.id);
const typeaheadComboboxId = computed(() => `${selectId.value}_typeahead__combobox`);
const typeaheadSearchId = computed(() => `${selectId.value}_typeahead__search`);
const typeaheadListboxId = computed(() => `${selectId.value}_typeahead__listbox`);
const typeaheadInstructionsId = computed(() => `${selectId.value}_typeahead__instructions`);
const typeaheadAnnouncementId = computed(() => `${selectId.value}_typeahead__announcement`);
const getStringAttr = (value: unknown) => {
  return typeof value === "string" && value.trim() ? value.trim() : undefined;
};
const inheritedAriaLabel = computed(() => getStringAttr(attrs["aria-label"]));
const typeaheadAriaLabelledBy = computed<string | undefined>(() => {
  // When labelId is provided, the parent already has <label for="..."> pointing
  // to the input by id. Adding aria-labelledby on top causes VoiceOver (especially
  // on Firefox) to announce the label twice. So only use aria-labelledby when it
  // comes from an inherited attr (no <label for> in that case).
  if (props.labelId?.trim()) {
    return undefined;
  }
  return getStringAttr(attrs["aria-labelledby"]);
});
const typeaheadAriaLabel = computed<string | undefined>(() => {
  if (props.labelId?.trim() || typeaheadAriaLabelledBy.value) {
    return undefined;
  }
  return inheritedAriaLabel.value;
});
const typeaheadDescribedBy = computed(() => {
  return [typeaheadInstructionsId.value, getStringAttr(attrs["aria-describedby"])]
    .filter(Boolean)
    .join(" ");
});
const hasInput = computed(() => query.value !== "");
const isPlaceholder = computed(() => {
  return model.value === null && isDropDownOpen.value === false;
});
const dynamicStyles = computed(() => {
  return {
    "--dropdown-height": props.height,
    "--highlight-bg-color": `var(--rds-${props.highlightBgColor})`,
    "--highlight-text-color": `var(--rds-${props.highlightTextColor})`
  };
});

const ghostOpacityDynamicStyles = computed(() => {
  return {
    "--typeahead-preselect-opacity": props.preselectOpacity
  };
});

const actionClasses = computed(() => {
  return `text-${props.actionVarint}`;
});

const functionRef = (el: Element | globalThis.ComponentPublicInstance | null, index: number) => {
  resultElements.value[index] = el;
};

/*
 ********************************************
 *  watch
 ********************************************
 */
watch(query, (newVal) => {
  emits("searching", newVal);
  hoverIndex.value = 0;
});

watch(model, (val) => {
  if (val == null) {
    handleDropDownClose();
    selectedElement.value = null;
  }
});

watch(hoverIndex, () => {
  selectedElement.value = resultElements.value[hoverIndex.value] as HTMLElement;
  if (selectedElement.value && selectedElement.value.scrollIntoView) {
    selectedElement.value.scrollIntoView({ block: "nearest" });
  }
});

watch(isDropDownOpen, (val) => {
  if (val === true) {
    const liveRegion = document.getElementById(typeaheadAnnouncementId.value);
    if (liveRegion) {
      liveRegion.textContent = "Expanded. Use arrow keys to navigate options.";
    }
    emits("shown");
  } else {
    emits("hidden");
  }
});

/*
 ********************************************
 *  Methods
 ********************************************
 */
const handleOnInput = (e: Event) => {
  if (props.disabled) {
    return;
  }
  query.value = (e.target as HTMLInputElement).value;
  isDropDownOpen.value = true;
};
const handleDropdownClick = (e: Event) => {
  if (props.disabled) {
    return;
  }
  if (isDropDownOpen.value === true) {
    isDropDownOpen.value = false;
  } else {
    hoverIndex.value = 0;
    if (model.value !== null && !hasInput.value) {
      const selectedIndex = results.value.findIndex(
        (result: FuseResult<T>) => result.item.value === model.value?.value
      );
      hoverIndex.value = selectedIndex !== -1 ? selectedIndex : 0;
    }
    typeaheadQuery.value?.focus();
    isDropDownOpen.value = true;
  }
};
const clearSelected = () => {
  if (props.disabled) {
    return;
  }
  model.value = null;
  query.value = "";
  typeaheadQuery.value?.focus();
  emits("cleared");
};
const handleChangeOption = (option: OptionType) => {
  model.value = option;
  handleDropDownClose();
};
const handleHover = (i: number, item: OptionType) => {
  hoverIndex.value = i;
  isHoverActive.value = true;
};
const handleTypeAheadFocus = () => {
  isFocused.value = true;
};

const handleTypeAheadBlur = () => {
  isFocused.value = false;
};

const handleDropDownClose = () => {
  isDropDownOpen.value = false;
  isFocused.value = false;
};

const handleCollapseHide = (el: Element) => {
  (el as HTMLElement).style.pointerEvents = "none";
};
const handleCollapseShown = (el: Element) => {
  (el as HTMLElement).style.pointerEvents = "auto";
};
watch(model, (newValue) => {
  emits("changed", newValue);
});
onBeforeUpdate(() => {
  searchOptions.value = props.options;
});
</script>
<script lang="ts">
export default {
  inheritAttrs: false
};
</script>
<style lang="scss" scoped>
.typeahead--open {
  .typeahead__selected {
    position: absolute;
    opacity: var(--typeahead-preselect-opacity);
  }
}
.typeahead {
  position: relative;
  .is-placeholder {
    opacity: var(--typeahead-preselect-opacity);
  }
  &--disabled {
    opacity: var(--typeahead-preselect-opacity);
    pointer-events: none;
    cursor: not-allowed;
  }
  &__dropdown-toggle {
    display: flex;
    background-color: var(--rds-body-bg);
    border: 1px solid var(--rds-border-color);
    border-radius: 0;
    white-space: normal;
    padding: 8px;
  }
  &__selected-options {
    display: flex;
    flex-basis: 100%;
    flex-grow: 1;
    flex-wrap: nowrap;
    padding: 0 2px;
    position: relative;
    overflow: hidden;
  }
  &__selected {
    display: flex;
    align-self: center;
    font-size: var(--rds-font-size);
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 120px;
    position: absolute;
    pointer-events: none;
  }
  &__actions {
    display: flex;
    align-items: center;
  }
  &__search {
    flex-grow: 1;
    padding: 0;
    margin: 0;
    position: relative;
    z-index: 5;
    background-color: transparent;
    font-size: 1rem;
    line-height: 1.5;
    color: var(--rds-text-color);
    appearance: none;
    border: 0;
    box-shadow: none;
    z-index: 1;
    width: 100%;
    max-width: 100%;
    outline: none;
  }
  &__action {
    appearance: none;
    padding: 0;
    border: 0;
    background-color: transparent;
    cursor: pointer;
    display: flex;
    align-items: center;

    & svg {
      height: 1.5rem;
      width: 24px !important;
      transition: transform 0.15s;
    }
  }
  &__dropdown {
    &-menu {
      width: 100%;
      z-index: 1000;
      padding: 5px 0;
      margin: 0.125rem 0 0;
      font-size: 1rem;
      color: var(--rds-text-color);
      text-align: left;
      list-style: none;
      background-color: var(--rds-body-bg);
      background-clip: padding-box;
      border: var(--rds-border-width) solid var(--rds-border-color);
      border-top: 0;
      border-radius:
        0 0 var(--rds-border-radius-sm),
        var(--rds-border-radius-sm);
      max-height: var(--dropdown-height);
      max-height: min(var(--dropdown-height), 52vh);
      margin: 0;
      min-width: 100%;
      vertical-align: top;
      overflow-y: auto;
      box-shadow: 0px 3px 6px 0px rgba(0, 0, 0, 0.15);
    }
    &-option {
      padding: 8px 12px;
      cursor: pointer;
      background-color: var(--rds-body-bg);
      color: var(--rds-text-color);
      &--highlighted {
        background-color: var(--highlight-bg-color);
        color: var(--highlight-text-color);
      }
      &--empty {
        padding: 8px 12px;
        color: var(--rds-text-color);
      }
    }
  }
}
.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

button.typeahead__action {
  transform: translateZ(0);
  isolation: isolate;
}

@media (max-width: 575.98px) {
  .typeahead__dropdown-menu {
    max-height: min(var(--dropdown-height), 34dvh);
    overscroll-behavior: contain;
  }
}
</style>
